import {
  FC,
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Link, useNavigate } from 'react-router-dom';

import { useUserContext } from '@/contexts/user';
import { Wallet } from '@/lib/apollo/types/gen/graphql';
import {
  getShowWalletInfo,
  ShowWalletInfoSessionStorage,
} from '@/storages/nav/fromData';

import NonMember from '../NonMember';
import {
  BalanceWrapper,
  DrawerButton,
  DrawerIcon,
  NavWrapper,
  NonMemberArea,
  NonMemberWrapper,
  PcBox,
  RightBox,
  ScrollItem,
  ScrollLink,
  ScrollList,
  ScrollNavWrapper,
  ScrollWrapper,
  SpBox,
  TotalBalance,
  WalletMark,
  WalletToggleIcon,
  WalletWrapper,
} from '../style';
import WalletInfo, { BalanceType } from '../WalletInfo';

const Menu = lazy(() => import('../Menu'));

const memberMenuLinks = [
  { text: '出走表', to: '/racecard' },
  { text: '投票履歴', to: '/bets/history' },
  { text: 'キャンペーン', to: '/current_campaign' },
  { text: '選手検索', to: '/racer/search' },
  { text: 'マイページ', to: '/my/dashboard' },
];

const nonMemberMenuLinks = [
  { text: 'はじめての方へ', to: '/guide' },
  { text: '出走表', to: '/racecard' },
  { text: 'キャンペーン', to: '/current_campaign' },
  { text: '選手検索', to: '/racer/search' },
];

const Wallets: FC<{
  handleOpenQuickChargeModal: () => void;
}> = ({ handleOpenQuickChargeModal }) => {
  const navigate = useNavigate();
  const wallet = useUserContext().user.wallet as Wallet;

  return (
    <>
      <WalletInfo
        balanceType={BalanceType.Cash}
        balance={wallet.balance}
        handleClick={() => navigate('/wallet')}
      />
      <WalletInfo
        balanceType={BalanceType.Toreta}
        balance={wallet.toreta.balance}
        handleClick={() => handleOpenQuickChargeModal()}
      />
    </>
  );
};

interface SlideNavProps {
  handleOpenQuickChargeModal: () => void;
  isOpenDrawer: boolean;
  setIsOpenDrawer: (bool: boolean) => void;
}

const SlideNav: FC<SlideNavProps> = ({
  handleOpenQuickChargeModal,
  isOpenDrawer,
  setIsOpenDrawer,
}) => {
  const { user, isMember } = useUserContext();
  const showWalletStorage = ShowWalletInfoSessionStorage;
  const [showWalletInfo, setShowWalletInfo] = useState<boolean>(
    getShowWalletInfo(),
  );
  const ref = useRef<HTMLElement>(null);

  const closeMenu = useCallback(() => {
    setIsOpenDrawer(false);
  }, []);

  const switchShowWallet = (setVal: boolean) => {
    setShowWalletInfo(setVal);
    showWalletStorage.set(setVal);
  };

  const totalBalance = useMemo(() => {
    return user.wallet ? user.wallet.balance + user.wallet.toreta.balance : 0;
  }, [user.wallet?.balance, user.wallet?.toreta.balance]);

  return (
    <>
      <NavWrapper ref={ref}>
        <ScrollNavWrapper>
          <ScrollWrapper>
            <ScrollList>
              {isMember ? (
                <>
                  {memberMenuLinks.map((ml, idx) => (
                    <ScrollItem key={idx}>
                      <ScrollLink>
                        <Link to={ml.to}>{ml.text}</Link>
                      </ScrollLink>
                    </ScrollItem>
                  ))}
                </>
              ) : (
                <>
                  {nonMemberMenuLinks.map((ml, idx) => (
                    <ScrollItem key={idx}>
                      <ScrollLink>
                        <Link to={ml.to}>{ml.text}</Link>
                      </ScrollLink>
                    </ScrollItem>
                  ))}
                </>
              )}
            </ScrollList>
          </ScrollWrapper>
        </ScrollNavWrapper>
        <RightBox>
          {isMember ? (
            <>
              <PcBox data-testid="wallet">
                <Wallets
                  handleOpenQuickChargeModal={() =>
                    handleOpenQuickChargeModal()
                  }
                />
              </PcBox>
            </>
          ) : (
            <PcBox>
              <NonMemberWrapper data-testid="non-member-info">
                <NonMember />
              </NonMemberWrapper>
            </PcBox>
          )}
          {!isMobileOnly && (
            <DrawerButton
              type="button"
              onClick={() => setIsOpenDrawer(!isOpenDrawer)}
              data-testid="drawer-button"
              aria-label={`${isOpenDrawer ? 'close' : 'open'}-menu-button`}
              aria-expanded={isOpenDrawer}
              aria-haspopup="true"
              aria-controls="dropdown-menu"
            >
              <DrawerIcon isOpen={isOpenDrawer} />
            </DrawerButton>
          )}
        </RightBox>
      </NavWrapper>
      {isMember ? (
        <SpBox>
          <WalletWrapper data-testid="wallet-sp" isOpen={showWalletInfo}>
            <Wallets
              handleOpenQuickChargeModal={() => handleOpenQuickChargeModal()}
            />
          </WalletWrapper>
          <BalanceWrapper onClick={() => switchShowWallet(!showWalletInfo)}>
            <WalletToggleIcon
              data-testid="wallet-toggle-icon"
              isOpen={showWalletInfo}
            />
            {showWalletInfo && (
              <TotalBalance data-testid="total-balance">
                <WalletMark />
                {totalBalance.toLocaleString()}円
              </TotalBalance>
            )}
          </BalanceWrapper>
        </SpBox>
      ) : (
        <NonMemberArea data-testid="non-member-info">
          <NonMember />
        </NonMemberArea>
      )}
      <Suspense fallback={<></>}>
        <Menu
          isActive={isOpenDrawer}
          closeMenu={closeMenu}
          aria-labelledby="dropdown-menu"
          aria-hidden={!isOpenDrawer}
        />
      </Suspense>
    </>
  );
};

export default SlideNav;
